import React, { useState, useEffect } from 'react';
import portalApi, { getPortalErrorMessage } from '@/lib/portalApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Receipt, Calendar, DollarSign, RefreshCw, Search, FileText, CheckCircle, Clock } from 'lucide-react';

const statusColors = {
  PAID: 'bg-green-500/10 text-green-600 border-green-500/20',
  PENDING: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  SENT: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  OVERDUE: 'bg-red-500/10 text-red-600 border-red-500/20',
  DRAFT: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

function InvoiceCard({ invoice }) {
  const isPaid = invoice.status === 'PAID';
  const paidPercentage = invoice.totalAmount > 0 ? (invoice.paidAmount / invoice.totalAmount) * 100 : 0;

  return (
    <Card className="hover:border-primary/30 transition-colors" data-testid={`invoice-${invoice.invoiceNumber}`}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Icon */}
          <div className={`h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isPaid ? 'bg-green-500/10' : 'bg-primary/10'
          }`}>
            {isPaid ? (
              <CheckCircle className="h-6 w-6 text-green-500" />
            ) : (
              <Receipt className="h-6 w-6 text-primary" />
            )}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="font-semibold">{invoice.invoiceNumber}</h3>
                <p className="text-sm text-muted-foreground">
                  {invoice.date ? new Date(invoice.date).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <Badge variant="outline" className={statusColors[invoice.status] || ''}>
                {invoice.status}
              </Badge>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm">
              {invoice.periodFrom && invoice.periodTo && (
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(invoice.periodFrom).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                  {' - '}
                  {new Date(invoice.periodTo).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                </div>
              )}
            </div>
          </div>

          {/* Amount */}
          <div className="text-right sm:min-w-[120px]">
            <p className="text-xl font-bold">${invoice.totalAmount?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
            {invoice.paidAmount > 0 && invoice.paidAmount < invoice.totalAmount && (
              <p className="text-xs text-muted-foreground">
                Paid: ${invoice.paidAmount?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InvoiceSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-xl" />
          <div className="flex-1">
            <Skeleton className="h-5 w-24 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-8 w-20" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function PortalInvoicesPage() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await portalApi.get('/portal/participant/invoices?limit=50');
      setInvoices(res.data.data || []);
    } catch (err) {
      toast.error(getPortalErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const filteredInvoices = invoices.filter(inv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return inv.invoiceNumber?.toLowerCase().includes(query);
  });

  // Calculate summary
  const totalAmount = invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
  const paidAmount = invoices.reduce((sum, inv) => sum + (inv.paidAmount || 0), 0);
  const pendingCount = invoices.filter(inv => inv.status !== 'PAID').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Invoices</h1>
          <p className="text-muted-foreground">Your billing history</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchInvoices} disabled={loading} data-testid="refresh-invoices">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {!loading && invoices.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Receipt className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Invoiced</p>
                  <p className="text-xl font-bold">${totalAmount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                  <p className="text-xl font-bold">${paidAmount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-xl font-bold">{pendingCount} invoice{pendingCount !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by invoice number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-invoices"
            />
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => <InvoiceSkeleton key={i} />)}
        </div>
      ) : filteredInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-lg font-medium">No Invoices Found</p>
            <p className="text-sm text-muted-foreground">
              {searchQuery ? 'Try adjusting your search' : 'No invoices on record'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredInvoices.map((invoice, idx) => (
            <InvoiceCard key={idx} invoice={invoice} />
          ))}
        </div>
      )}
    </div>
  );
}
